import { useApp } from '@/context/AppContext'
import { useFilteredBreakValues } from '@/hooks/useFilteredBreakValues'
import { HorizontalStackedBar } from '@/components/charts/HorizontalStackedBar'
import { BarGrid } from '@/components/charts/BarGrid'
import { buildFilterKey } from '@/services/filterEngine'
import { TABLEAU_10, getResponseColor } from '@/utils/colors'

// Helper to format question text with instructions/notes styled differently
function formatQuestionText(text: string): React.ReactNode {
  // Patterns to style as instructions (italics, grayed out)
  const instructionPatterns = [
    /\s*(Select ALL that apply\.?)/gi,
    /\s*(UNAIDED)/gi,
    /\s*(AIDED)/gi,
    /\s*(Multiple response\.?)/gi,
  ]

  let result: React.ReactNode[] = []
  let remaining = text
  let key = 0

  for (const pattern of instructionPatterns) {
    const match = remaining.match(pattern)
    if (match && match[1]) {
      const idx = remaining.indexOf(match[0])
      if (idx > 0) {
        result.push(<span key={key++}>{remaining.slice(0, idx)}</span>)
      }
      result.push(
        <span key={key++} className="italic text-slate-400 dark:text-slate-500 text-base font-normal">
          {match[1]}
        </span>
      )
      remaining = remaining.slice(idx + match[0].length)
    }
  }

  if (remaining) {
    result.push(<span key={key++}>{remaining}</span>)
  }

  return result.length > 0 ? result : text
}

// Question IDs for Demographics Details page (Industry first, News last per Tableau)
// Note: Ethnic Background removed - it's already shown on Demographics General page
const DEMOGRAPHICS_DETAILS_QUESTIONS = [
  { id: 'Demo_Industry', label: 'Income Source' },
  { id: 'Demo_Home Ownership', label: 'Home Ownership Status' },
  { id: 'Demo_Church Attendance', label: 'Church Attendance' },
  { id: 'Demo_News Sources', label: 'News and Information Sources' },
] as const

// Labels to exclude from Industry of Employment
const EXCLUDED_INDUSTRY_LABELS = ['Other Industry', 'Other industry', 'Other']

// Industry order by environmental footprint (heaviest to lightest)
// Labels match aggregate script Demo_Industry short labels
const INDUSTRY_ORDER = [
  // Grays (darkest to lightest)
  'Coal',
  'Oil & Gas',
  'Construction/Manufacturing',
  'Transport/Logistics',
  // Browns (darkest to lightest)
  'Forestry',
  'Fishing',
  'Food & Agriculture',
  'Renewable Energy',
  // Blues (darkest to lightest)
  'Finance/Insurance',
  'Real Estate',
  'Professional Services',
  'Tourism',
  'Retail',
  // Greens (darkest to lightest)
  'Arts/Culture',
  'Healthcare',
  'Education/Training',
]

// Industry colors grouped by environmental category (least→most green)
// Gray: Coal, Oil, Construction, Transport (extraction/heavy industry)
// Brown: Forestry, Fishing (natural resource)
// Orange: Food/Ag, Renewable (transitional/sustainable production)
// Blue: Finance, Real Estate, Tourism, Retail (services)
// Green: Arts, Education, Healthcare (social/creative - most green)
const INDUSTRY_COLORS: Record<string, string> = {
  // Grays - heavy industry (least green)
  'Coal': '#1f1f1f',                    // charcoal (darkest)
  'Oil & Gas': '#4a4a4a',               // dark gray
  'Construction/Manufacturing': '#6b6b6b', // medium gray
  'Transport/Logistics': '#8c8c8c',     // light gray
  // Browns - natural resource extraction (darkest to lightest)
  'Forestry': '#5c4033',                // dark brown
  'Fishing': '#8b5a2b',                 // medium brown/tan
  'Food & Agriculture': '#a67c52',      // light brown/tan
  'Renewable Energy': '#d4a574',        // lightest brown/golden
  // Blues - service industries (darkest to lightest)
  'Finance/Insurance': '#1e3a5f',       // dark navy
  'Real Estate': '#2563eb',             // royal blue
  'Professional Services': '#3b82f6',   // medium blue
  'Tourism': '#60a5fa',                 // light blue
  'Retail': '#93c5fd',                  // sky blue (lightest)
  // Greens - social/creative sectors (darkest to lightest)
  'Arts/Culture': '#166534',            // dark green
  'Healthcare': '#22c55e',              // medium green
  'Education/Training': '#4ade80',      // light green (lightest)
}

// Church attendance order (most frequent → least frequent, Never on far right)
const CHURCH_ATTENDANCE_ORDER = [
  'More than once a week',
  'About once a week',
  'Once or twice a month',
  'A few times a year',
  'Seldom',
  'Never',
]

// Church attendance colors (gold for most godly → gradations to gray for never)
const CHURCH_ATTENDANCE_COLORS: Record<string, string> = {
  'More than once a week': '#d4af37',  // gold
  'About once a week': '#c9a227',       // darker gold
  'Once or twice a month': '#b8962a',   // bronze-gold
  'A few times a year': '#a8a060',      // olive-gold
  'Seldom': '#9a9a7a',                  // gray-olive
  'Never': '#8c8c8c',                   // gray
}

// News sources order (most popular → least popular)
const NEWS_SOURCES_ORDER = [
  'Facebook',       // 68.9%
  'YouTube',        // 64.0%
  'TV News',        // 54.7%
  'Instagram',      // 44.8%
  'Radio News',     // 32.4%
  'TikTok',         // 28.5%
  'Newspapers',     // 24.1%
  'Twitter/X',      // 19.8%
  'Podcasts',       // 18.7%
  'LinkedIn',       // 17.1%
  'Reddit',         // 16.7%
  'Twitch',         // 5.5%
  'BlueSky',        // 3.0%
  'None',           // 1.7%
]

// News sources colors by category - designed for high contrast in popularity order
const NEWS_SOURCES_COLORS: Record<string, string> = {
  // Traditional media (warm amber/gold tones)
  'TV News': '#b45309',                 // dark amber
  'Newspapers': '#d97706',              // amber
  'Radio News': '#fbbf24',              // gold/yellow
  // Video/streaming platforms (blue gradients)
  'YouTube': '#1e40af',                 // dark blue
  'Twitch': '#3b82f6',                  // medium blue
  'Podcasts': '#0ea5e9',                // cyan
  'TikTok': '#06b6d4',                  // teal
  // Social/micro-blogging (purple/pink gradients)
  'Facebook': '#7c3aed',                // violet
  'Twitter/X': '#a855f7',               // purple
  'Reddit': '#f97316',                  // orange-red (distinct from adjacent)
  'LinkedIn': '#db2777',                // pink
  'BlueSky': '#ec4899',                 // rose/magenta
  // Photo-sharing (green)
  'Instagram': '#059669',               // emerald
  // Other
  'None': '#6b7280',                    // gray
}

// BarGrid configs for multi-select questions (Industry and News Sources)
const INDUSTRY_GRID_CONFIG: Array<{ label: string; color: string }> = [
  { label: 'Coal', color: '#1f1f1f' },
  { label: 'Oil & Gas', color: '#4a4a4a' },
  { label: 'Construction/Manufacturing', color: '#6b6b6b' },
  { label: 'Transport/Logistics', color: '#8c8c8c' },
  { label: 'Forestry', color: '#5c4033' },
  { label: 'Fishing', color: '#8b5a2b' },
  { label: 'Food & Agriculture', color: '#a67c52' },
  { label: 'Renewable Energy', color: '#d4a574' },
  { label: 'Finance/Insurance', color: '#1e3a5f' },
  { label: 'Real Estate', color: '#2563eb' },
  { label: 'Professional Services', color: '#3b82f6' },
  { label: 'Tourism', color: '#60a5fa' },
  { label: 'Retail', color: '#93c5fd' },
  { label: 'Arts/Culture', color: '#166534' },
  { label: 'Healthcare', color: '#22c55e' },
  { label: 'Education/Training', color: '#4ade80' },
]

const NEWS_SOURCES_GRID_CONFIG: Array<{ label: string; color: string }> = [
  { label: 'Facebook', color: '#7c3aed' },
  { label: 'YouTube', color: '#1e40af' },
  { label: 'TV News', color: '#b45309' },
  { label: 'Instagram', color: '#059669' },
  { label: 'Radio News', color: '#fbbf24' },
  { label: 'TikTok', color: '#06b6d4' },
  { label: 'Newspapers', color: '#d97706' },
  { label: 'Twitter/X', color: '#a855f7' },
  { label: 'Podcasts', color: '#0ea5e9' },
  { label: 'LinkedIn', color: '#db2777' },
  { label: 'Reddit', color: '#f97316' },
  { label: 'Twitch', color: '#3b82f6' },
  { label: 'BlueSky', color: '#ec4899' },
  { label: 'None', color: '#6b7280' },
]

export function DemographicsDetails() {
  const { state } = useApp()
  const { data, filters } = state
  const { selectedBreak, selectedProvince } = filters

  // Get break definition
  const breakDef = data?.breaks.find(b => b.id === selectedBreak)
  const { breakValues: filteredBreakValues } = useFilteredBreakValues()
  const breakValues = selectedBreak !== 'total' && breakDef ? filteredBreakValues : ['total']
  const isBreakActive = selectedBreak !== 'total'
  const breakName = breakDef?.name || 'Total'
  const displayMap = breakDef?.displayMap || {}

  // Get display name with alias support
  const getDisplayName = (value: string) => displayMap[value] || value

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-800 dark:text-white">
          Demographics Details
        </h1>
        <span className="text-sm text-slate-500 dark:text-slate-400">
          Break: {breakName}
        </span>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-6">
        <div className="space-y-8">
          {!data ? (
            <p className="text-slate-500 dark:text-slate-400">Loading...</p>
          ) : (
            DEMOGRAPHICS_DETAILS_QUESTIONS.map(({ id, label }) => {
              const question = data.questions[id]
              if (!question) {
                return (
                  <div key={id} className="border-b border-slate-100 dark:border-slate-700 pb-6 last:border-0 last:pb-0">
                    <h2 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-3">
                      {label}
                    </h2>
                    <p className="text-sm text-slate-400 dark:text-slate-500 italic">
                      Data not available
                    </p>
                  </div>
                )
              }

              // Build rows for each break value
              const rows = breakValues.map(value => {
                const filterKey = buildFilterKey(
                  'total',
                  selectedBreak,
                  isBreakActive ? value : undefined,
                  selectedProvince
                )
                let questionData = question.data[filterKey]

                // Fallback: try without province filter if no data found
                if (!questionData && selectedProvince) {
                  const fallbackKey = buildFilterKey('total', selectedBreak, isBreakActive ? value : undefined)
                  questionData = question.data[fallbackKey]
                }
                return { value, filterKey, data: questionData }
              }).filter(row => row.data)

              if (rows.length === 0) {
                return (
                  <div key={id} className="border-b border-slate-100 dark:border-slate-700 pb-6 last:border-0 last:pb-0">
                    <h2 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-3">
                      {label}
                    </h2>
                    <p className="text-sm text-slate-400 dark:text-slate-500 italic">
                      No data for current filters
                    </p>
                  </div>
                )
              }

              // Filter and sort responses for Industry, Church Attendance, and News Sources
              const isIndustry = id === 'Demo_Industry'
              const isChurchAttendance = id === 'Demo_Church Attendance'
              const isNewsSources = id === 'Demo_News Sources'

              const filterResponses = (responses: typeof rows[0]['data']['responses']) => {
                let result = responses

                if (isIndustry) {
                  // Filter out excluded labels and sort by environmental footprint
                  result = responses.filter(r => !EXCLUDED_INDUSTRY_LABELS.includes(r.label))
                  result = result.sort((a, b) => {
                    const aIdx = INDUSTRY_ORDER.indexOf(a.label)
                    const bIdx = INDUSTRY_ORDER.indexOf(b.label)
                    if (aIdx === -1) return 1
                    if (bIdx === -1) return -1
                    return aIdx - bIdx
                  })
                } else if (isChurchAttendance) {
                  // Sort by frequency (most → least, Never on far right)
                  result = [...responses].sort((a, b) => {
                    const aIdx = CHURCH_ATTENDANCE_ORDER.indexOf(a.label)
                    const bIdx = CHURCH_ATTENDANCE_ORDER.indexOf(b.label)
                    if (aIdx === -1) return 1
                    if (bIdx === -1) return -1
                    return aIdx - bIdx
                  })
                } else if (isNewsSources) {
                  // Sort by popularity (most popular first)
                  result = [...responses].sort((a, b) => {
                    const aIdx = NEWS_SOURCES_ORDER.indexOf(a.label)
                    const bIdx = NEWS_SOURCES_ORDER.indexOf(b.label)
                    if (aIdx === -1) return 1
                    if (bIdx === -1) return -1
                    return aIdx - bIdx
                  })
                }

                return result
              }

              // Get legend labels from first row (filtered)
              const legendLabels = filterResponses(rows[0]?.data?.responses || []).map(r => r.label)
              const needsCyclingColors = question.scaleType === 'multiselect'

              // Color getter for legend and bars
              const getColorForLabel = (lbl: string, idx: number) => {
                if (isIndustry) {
                  return INDUSTRY_COLORS[lbl] || '#52b788'
                }
                if (isChurchAttendance) {
                  return CHURCH_ATTENDANCE_COLORS[lbl] || '#bab0ac'
                }
                if (isNewsSources) {
                  return NEWS_SOURCES_COLORS[lbl] || '#6b7280'
                }
                if (needsCyclingColors) {
                  return TABLEAU_10[idx % TABLEAU_10.length]
                }
                return getResponseColor(question.scaleType || 'demographic', lbl)
              }

              // Use BarGrid for multi-select questions (Industry and News Sources)
              const isMultiSelect = isIndustry || isNewsSources

              if (isMultiSelect) {
                // Get config for this question
                const gridConfig = isIndustry
                  ? INDUSTRY_GRID_CONFIG
                  : NEWS_SOURCES_GRID_CONFIG

                // Build columns from config (only include items that exist in data)
                const sampleData = rows[0]?.data?.responses || []
                const dataLabels = new Set(sampleData.map(r => r.label))

                const columns = gridConfig
                  .filter(c => dataLabels.has(c.label))
                  .map(c => ({
                    id: c.label,
                    label: c.label,
                    color: c.color,
                  }))

                // Build rows for each break value
                const gridRows = rows.map(row => {
                  const values: Record<string, number> = {}
                  row.data!.responses.forEach(r => {
                    values[r.label] = r.percentage
                  })
                  return {
                    id: row.value,
                    label: isBreakActive ? getDisplayName(row.value) : 'National Sample',
                    values,
                  }
                })

                return (
                  <div key={id} className="border-b border-slate-100 dark:border-slate-700 pb-6 last:border-0 last:pb-0">
                    <BarGrid
                      title={formatQuestionText(question.text || label)}
                      description=""
                      columns={columns}
                      rows={gridRows}
                      colorIntensity={false}
                    />
                  </div>
                )
              }

              // Use HorizontalStackedBar for other questions
              return (
                <div key={id} className="border-b border-slate-100 dark:border-slate-700 pb-6 last:border-0 last:pb-0">
                  <h2 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-3">
                    {formatQuestionText(question.text || label)}
                  </h2>

                  {/* Legend */}
                  <div className="flex flex-wrap gap-x-3 gap-y-1 mb-3">
                    {legendLabels.map((lbl, idx) => (
                      <div key={lbl} className="flex items-center gap-1 text-xs" title={lbl}>
                        <div
                          className="w-3 h-3 rounded-sm flex-shrink-0"
                          style={{ backgroundColor: getColorForLabel(lbl, idx) }}
                        />
                        <span className="text-slate-600 dark:text-slate-400">
                          {lbl}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="space-y-2">
                    {rows.map(row => {
                      const filteredSegments = filterResponses(row.data!.responses)
                      return (
                        <div key={row.filterKey} className="flex items-center gap-2">
                          <div className="w-40 text-xs text-slate-500 dark:text-slate-400 text-right shrink-0 leading-tight" title={isBreakActive ? getDisplayName(row.value) : 'National Sample'}>
                            {isBreakActive ? getDisplayName(row.value) : 'National Sample'}
                          </div>
                          <div className="flex-1">
                            <HorizontalStackedBar
                              segments={filteredSegments}
                              scaleType={question.scaleType || 'demographic'}
                              height={isBreakActive ? 24 : 28}
                              showLabels={false}
                              normalize={true}
                              colorGetter={(needsCyclingColors || isChurchAttendance) ? getColorForLabel : undefined}
                            />
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}
